import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Copy, Layers, Zap, Users } from 'lucide-react';
import type { ApprovalWorkflow } from '@/types/workflow';

interface WorkflowDuplicateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workflow: ApprovalWorkflow | null;
  onDuplicate: (
    workflowId: string,
    newName: string,
    options: {
      copyStages: boolean;
      copyRules: boolean;
      copyAssignments: boolean;
    }
  ) => Promise<void>;
}

export function WorkflowDuplicateDialog({
  open,
  onOpenChange,
  workflow,
  onDuplicate,
}: WorkflowDuplicateDialogProps) {
  const [newName, setNewName] = useState('');
  const [copyStages, setCopyStages] = useState(true);
  const [copyRules, setCopyRules] = useState(true);
  const [copyAssignments, setCopyAssignments] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Auto-generate name and reset error when dialog opens
  React.useEffect(() => {
    if (open && workflow) {
      setNewName(`${workflow.name} (Copy)`);
      setErrorMessage(null);
    }
  }, [open, workflow]);

  const isNameValid = newName.trim().length >= 3 && newName.trim().length <= 200;

  const handleDuplicate = async () => {
    if (!workflow || !isNameValid) return;

    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      await onDuplicate(workflow.id, newName.trim(), {
        copyStages,
        copyRules,
        copyAssignments,
      });
      onOpenChange(false);
      // Reset state
      setNewName('');
      setCopyStages(true);
      setCopyRules(true);
      setCopyAssignments(false);
      setErrorMessage(null);
    } catch (error) {
      console.error('Failed to duplicate workflow:', error);
      setErrorMessage(
        error instanceof Error ? error.message : 'Failed to duplicate workflow. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
    // Reset state
    setNewName('');
    setCopyStages(true);
    setCopyRules(true);
    setCopyAssignments(false);
    setErrorMessage(null);
  };

  if (!workflow) return null;

  const stageCount = workflow.stages?.length || 0;
  const ruleCount = workflow.stages?.reduce(
    (total: number, stage: ApprovalWorkflow['stages'][number]) => total + (stage.automationRules?.length || 0),
    0
  ) || 0;
  const assignmentCount = workflow.totalApprovers || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="h-5 w-5" />
            Duplicate Workflow
          </DialogTitle>
          <DialogDescription>
            Create a copy of "{workflow.name}" with the options below.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* New Workflow Name */}
          <div className="space-y-2">
            <Label htmlFor="workflow-name">
              New Workflow Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="workflow-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Enter workflow name"
              maxLength={200}
              autoFocus
            />
            {newName.trim().length === 0 && (
              <p className="text-xs text-muted-foreground">
                A name is required for the new workflow
              </p>
            )}
            {newName.trim().length > 0 && newName.trim().length < 3 && (
              <p className="text-xs text-destructive">
                Name must be at least 3 characters
              </p>
            )}
            {newName.trim().length > 200 && (
              <p className="text-xs text-destructive">
                Name must be no more than 200 characters
              </p>
            )}
          </div>

          {/* Copy Options */}
          <div className="space-y-4">
            <Label>What to Copy</Label>

            {/* Copy Stages */}
            <div className="flex items-start gap-3 rounded-lg border p-4">
              <Checkbox
                id="copy-stages"
                checked={copyStages}
                onCheckedChange={(checked) => setCopyStages(checked as boolean)}
              />
              <div className="flex-1">
                <label
                  htmlFor="copy-stages"
                  className="flex items-center gap-2 font-medium cursor-pointer"
                >
                  <Layers className="h-4 w-4 text-blue-500" />
                  Approval Stages
                </label>
                <p className="text-xs text-muted-foreground mt-1">
                  Copy all {stageCount} stage{stageCount !== 1 ? 's' : ''} with their
                  configuration
                </p>
              </div>
            </div>

            {/* Copy Smart Rules */}
            <div className="flex items-start gap-3 rounded-lg border p-4">
              <Checkbox
                id="copy-rules"
                checked={copyRules}
                onCheckedChange={(checked) => setCopyRules(checked as boolean)}
                disabled={!copyStages}
              />
              <div className="flex-1">
                <label
                  htmlFor="copy-rules"
                  className={`flex items-center gap-2 font-medium ${
                    copyStages ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
                  }`}
                >
                  <Zap className="h-4 w-4 text-purple-500" />
                  Smart Rules
                </label>
                <p className="text-xs text-muted-foreground mt-1">
                  {ruleCount > 0
                    ? `Copy ${ruleCount} smart rule${ruleCount !== 1 ? 's' : ''}`
                    : 'No smart rules configured'}
                </p>
              </div>
            </div>

            {/* Copy User Assignments */}
            <div className="flex items-start gap-3 rounded-lg border p-4">
              <Checkbox
                id="copy-assignments"
                checked={copyAssignments}
                onCheckedChange={(checked) => setCopyAssignments(checked as boolean)}
              />
              <div className="flex-1">
                <label
                  htmlFor="copy-assignments"
                  className="flex items-center gap-2 font-medium cursor-pointer"
                >
                  <Users className="h-4 w-4 text-green-500" />
                  User Assignments
                </label>
                <p className="text-xs text-muted-foreground mt-1">
                  {assignmentCount > 0
                    ? `Copy assignments for ${assignmentCount} employee${
                        assignmentCount !== 1 ? 's' : ''
                      }`
                    : 'No users assigned'}
                </p>
              </div>
            </div>
          </div>

          {/* Warning about assignments */}
          {copyAssignments && assignmentCount > 0 && (
            <Alert>
              <AlertDescription>
                Users will be assigned to both the original and duplicated workflow.
                You may want to review assignments after duplication.
              </AlertDescription>
            </Alert>
          )}

          {/* Info about draft status */}
          <Alert>
            <AlertDescription>
              The duplicated workflow will be created as a <strong>Draft</strong> so you
              can review it before activating.
            </AlertDescription>
          </Alert>

          {/* Error message */}
          {errorMessage && (
            <Alert variant="destructive">
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleDuplicate}
            disabled={!isNameValid || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Copy className="h-4 w-4 mr-2 animate-spin" />
                Duplicating...
              </>
            ) : (
              <>
                <Copy className="h-4 w-4 mr-2" />
                Duplicate Workflow
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
